import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AppStateService } from '../../services/app-state.service';
import { MarketToken } from '../../models/types';
import * as echarts from 'echarts';

@Component({
  selector: 'app-token-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './token-detail.component.html'
})
export class TokenDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  token: MarketToken | undefined;
  timeframes = ['1H', '1D', '1W', '1M', '1Y', 'ALL'];
  activeTimeframe = '1D';
  public Math = Math;
  chartType: 'line' | 'bar' = 'line';
  chartInstance: echarts.ECharts | undefined;

  activeTab = 'Transactions';
  tabs = ['Transactions', 'Pools'];

  // Swap Widget State
  swapFromAmount = '';
  swapToAmount = '';

  @ViewChild('mainChart') chartRef!: ElementRef;

  constructor(
    private route: ActivatedRoute,
    public appState: AppStateService
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const symbol = params.get('symbol');
      if (symbol) {
        this.token = this.appState.getTokenBySymbol(symbol);
        if (this.chartInstance) {
          this.initChart();
        }
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => this.initChart(), 100);
  }

  ngOnDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  setTimeframe(tf: string) {
    this.activeTimeframe = tf;
    this.initChart();
  }

  setChartType(type: 'line' | 'bar') {
    this.chartType = type;
    this.initChart();
  }

  setTab(tab: string) {
    this.activeTab = tab;
  }

  initChart() {
    if (!this.chartRef || !this.token) return;
    
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
    
    this.chartInstance = echarts.init(this.chartRef.nativeElement);
    
    const isPositive = this.token.change1D >= 0;
    const color = isPositive ? '#14f195' : '#ef4444';
    
    // Generate dummy data based on token chartData but longer for main chart
    const baseData = this.token.chartData;
    const fullData = [];
    let currentVal = this.token.price * 0.9;
    for (let i = 0; i < 50; i++) {
       const change = (Math.random() - 0.45) * (this.token.price * 0.05);
       currentVal += change;
       fullData.push(currentVal);
    }
    // Make end match current price
    fullData[fullData.length - 1] = this.token.price;

    const option: echarts.EChartsOption = {
      grid: { left: 0, right: 40, top: 20, bottom: 0 },
      xAxis: { 
        type: 'category', 
        show: false, 
        boundaryGap: false 
      },
      yAxis: { 
        type: 'value', 
        position: 'right',
        splitLine: { show: true, lineStyle: { color: 'rgba(255,255,255,0.05)', type: 'dashed' } },
        axisLabel: { color: '#888', formatter: '${value}', fontSize: 10, margin: 15 }
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(0,0,0,0.8)',
        borderColor: 'rgba(255,255,255,0.1)',
        textStyle: { color: '#fff' },
        formatter: (params: any) => {
           return `<div class="font-bold">\$${params[0].value.toFixed(4)}</div>`;
        }
      },
      series: [{
        type: this.chartType,
        data: fullData,
        smooth: 0.1,
        showSymbol: false,
        lineStyle: { width: 2, color: color },
        itemStyle: { color: color },
        areaStyle: this.chartType === 'line' ? {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: isPositive ? 'rgba(20, 241, 149, 0.2)' : 'rgba(239, 68, 68, 0.2)' },
            { offset: 1, color: 'rgba(0, 0, 0, 0)' }
          ])
        } : undefined
      }],
      animation: false
    };
    
    this.chartInstance.setOption(option);
  }
}
